import { useQuery } from '@apollo/client';
import { useApiKeys } from '@merchant-portal/credentials';
import { selectors as experimentationSelectors } from '@merchant-portal/experimentation';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { GET_API_CREDENTIALS } from './qqlQueries';
import { hasActiveApikey } from './utils/hasActiveApikey';
const BOOST_API_KEYS_ENABLED_EXPERIMENT_KEY = 'merchant-portal.boost.gkd-api-keys.enabled';
/**
 * This hook is used to check if the merchant has already active API keys.
 * The way to check this is different depending on the feature toggle `BOOST_API_KEYS_ENABLED_EXPERIMENT_KEY`.
 * If the feature is enabled, we need to check the API keys using the `useApiKeys` hook.
 * If the feature is disabled, we need to check the API keys using the `GET_API_CREDENTIALS` graphql query
 */
export const useHasActiveApiKeys = ({ mid }) => {
    const [hasAlreadyActiveKeys, setHasAlreadyActiveKeys] = useState(false);
    const isGKDApiKeysEnabled = useSelector(experimentationSelectors.isFeatureToggleEnabled(BOOST_API_KEYS_ENABLED_EXPERIMENT_KEY));
    const { loading, data, error } = useQuery(GET_API_CREDENTIALS, {
        variables: { merchantId: mid },
        skip: isGKDApiKeysEnabled // skip the query if the feature is enabled
    });
    const { getApiKeys } = useApiKeys({ mid: mid !== null && mid !== void 0 ? mid : '', fetchingApiKeysInitially: false });
    useEffect(() => {
        if (!isGKDApiKeysEnabled || !mid)
            return;
        let isCancelled = false;
        getApiKeys()
            .then(apiKeys => {
            if (isCancelled)
                return;
            setHasAlreadyActiveKeys(apiKeys.some(apiKey => apiKey.keyType === 'API' && apiKey.enabled === true));
        })
            .catch(() => {
            if (isCancelled)
                return;
            setHasAlreadyActiveKeys(false);
        });
        return () => {
            isCancelled = true;
        };
    }, [isGKDApiKeysEnabled, mid]);
    if (!mid) {
        return {
            hasAlreadyActiveKeys: false,
            setHasAlreadyActiveKeys
        };
    }
    if (!isGKDApiKeysEnabled) {
        if (loading || error)
            return {
                hasAlreadyActiveKeys: false,
                setHasAlreadyActiveKeys
            };
        return {
            hasAlreadyActiveKeys: hasActiveApikey(data),
            setHasAlreadyActiveKeys
        };
    }
    return {
        hasAlreadyActiveKeys,
        setHasAlreadyActiveKeys
    };
};
